import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

import { MDBoxProps } from ".";

interface OwnerState {
  variant: "contained" | "gradient";
  bgColor:
    | "transparent"
    | "white"
    | "black"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "text"
    | "grey-100"
    | "grey-200"
    | "grey-300"
    | "grey-400"
    | "grey-500"
    | "grey-600"
    | "grey-700"
    | "grey-800"
    | "grey-900";
  color:
    | "transparent"
    | "white"
    | "black"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "text"
    | "grey-100"
    | "grey-200"
    | "grey-300"
    | "grey-400"
    | "grey-500"
    | "grey-600"
    | "grey-700"
    | "grey-800"
    | "grey-900"
    | "inherit";
  opacity: number;
  borderRadius?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "section";
  shadow?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "inset";
  coloredShadow:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "none";
}

export default styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerState",
})<MDBoxProps & { ownerState: OwnerState }>(({ theme, ownerState }) => {
  const { variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow } = ownerState;

  // Usar cores básicas do Material UI
  const getBackgroundColor = () => {
    if (bgColor === "transparent") return "transparent";
    if (bgColor === "white") return "#ffffff";
    if (bgColor === "black") return "#000000";

    // Tentar usar as cores do tema
    if (theme?.palette?.[bgColor as keyof typeof theme.palette]) {
      const paletteColor = theme.palette[bgColor as keyof typeof theme.palette] as any;
      if (paletteColor?.main) return paletteColor.main;
    }

    // Cores padrão como fallback
    return bgColor;
  };

  const getTextColor = () => {
    if (color === "transparent") return "transparent";
    if (color === "white") return "#ffffff";
    if (color === "black") return "#000000";
    if (color === "inherit") return "inherit";

    // Tentar usar as cores do tema
    if (theme?.palette?.[color as keyof typeof theme.palette]) {
      const paletteColor = theme.palette[color as keyof typeof theme.palette] as any;
      if (paletteColor?.main) return paletteColor.main;
    }

    // Cor padrão como fallback
    return "#7B809A";
  };

  const getBorderRadius = () => {
    const radiusMap = {
      xs: "4px",
      sm: "8px",
      md: "12px",
      lg: "16px",
      xl: "20px",
      xxl: "24px",
      section: "16px",
    };

    return borderRadius ? radiusMap[borderRadius] : "0";
  };

  const getBoxShadow = () => {
    if (!shadow) return "none";

    const shadowMap = {
      xs: "0 1px 3px rgba(0,0,0,0.12)",
      sm: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
      md: "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
      lg: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
      xl: "0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)",
      xxl: "0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
      inset: "inset 0 1px 3px rgba(0,0,0,0.12)",
    };

    return shadowMap[shadow] || "none";
  };

  return {
    opacity,
    backgroundColor: getBackgroundColor(),
    color: getTextColor(),
    borderRadius: getBorderRadius(),
    boxShadow: getBoxShadow(),
  };
});
